import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { pluggyPendenciasApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import type { PluggyPendenciaInfo } from '../../types'
import { invalidarPluggy, soNovas } from './invalidar'

export function AceitarTudoButton({ pendencias, variant = 'light', className }: {
  pendencias: PluggyPendenciaInfo[]
  variant?: 'light' | 'soft-primary'
  className?: string
}) {
  const novas = soNovas(pendencias)
  const qc = useQueryClient()
  const toast = useToast()
  const [aberto, setAberto] = useState(false)
  const [rodando, setRodando] = useState(false)
  const [feitas, setFeitas] = useState(0)
  const [resultado, setResultado] = useState<{ ok: number; falhas: number; primeiroErro?: string } | null>(null)

  function fechar() {
    if (rodando) return
    setAberto(false)
    setResultado(null)
    setFeitas(0)
  }

  async function aceitar() {
    const fila = [...novas]
    setRodando(true)
    setFeitas(0)
    let ok = 0
    let falhas = 0
    let primeiroErro: string | undefined
    for (const p of fila) {
      try {
        await pluggyPendenciasApi.aceitar(p.id)
        ok++
      } catch (e) {
        falhas++
        primeiroErro ??= e instanceof Error ? e.message : undefined
      }
      setFeitas(ok + falhas)
    }
    setRodando(false)
    setResultado({ ok, falhas, primeiroErro })
    invalidarPluggy(qc)
    if (falhas) toast.error(`${falhas} pendência(s) não puderam ser aceitas.`)
    else toast.success(`${ok} lançamento(s) aceitos.`)
  }

  if (novas.length === 0 && !aberto) return null

  return (
    <>
      <Button variant={variant} icon="ri-check-double-line" className={className} onClick={() => setAberto(true)}>
        Aceitar tudo ({novas.length} {novas.length === 1 ? 'nova' : 'novas'})
      </Button>
      <Modal
        open={aberto}
        onClose={fechar}
        title="Aceitar tudo"
        footer={resultado ? (
          <Button onClick={fechar}>Fechar</Button>
        ) : (
          <>
            <Button variant="light" onClick={fechar} disabled={rodando}>Cancelar</Button>
            <Button icon="ri-check-double-line" loading={rodando} onClick={aceitar}>Aceitar {novas.length}</Button>
          </>
        )}
      >
        {resultado ? (
          <div className="space-y-2 text-13">
            <p className="text-income"><i className="ri-check-line" aria-hidden /> {resultado.ok} aceita(s) com sucesso.</p>
            {resultado.falhas > 0 && (
              <p className="text-expense">
                <i className="ri-error-warning-line" aria-hidden /> {resultado.falhas} falhou(aram){resultado.primeiroErro ? `: ${resultado.primeiroErro}` : '.'} Elas continuam na caixa de entrada.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-13 text-muted">
            <p>
              Vamos aceitar as <strong className="text-white">{novas.length}</strong> pendências novas, uma por vez, do jeito que vieram do banco.
              Duplicatas, transferências, faturas e estornos continuam na caixa de entrada para você decidir um a um.
            </p>
            {rodando && <p className="text-white">Aceitando... {feitas} de {novas.length}</p>}
          </div>
        )}
      </Modal>
    </>
  )
}
